export type TrustLogo = {
  id: string;
  /** Company name, used as the accessible name of the mark. */
  label: string;
  /** Single-color SVG (`fill="currentColor"`), drawn through a CSS mask in the text color. */
  src: string;
  /** Width over height of the SVG viewBox, so every mark reserves its exact box. */
  aspectRatio: number;
  /**
   * Optical size: height relative to the band's logo height. Starts from equal
   * area (wide marks shorter, compact marks taller) and is tuned by eye so the
   * six read as equal weight on one baseline.
   */
  scale: number;
};

/**
 * The "Trusted by production teams" marks, one color each.
 *
 * Sources:
 * - Filmmaker Tools: official lockup https://www.filmmaker.tools/fmt_logo.png,
 *   traced to one color (clapper stripes cut out).
 * - End Cue: official logo on https://www.endcue.com/ (EndCue_Logo.png),
 *   traced to one color (letters cut out of the discs).
 * - Greencard: official logo on https://www.greencardnewyork.com/
 *   (greencard-logo_white.png), traced.
 * - Arte Facto: existing repo asset (public/media/trust/logo-04.png, removed),
 *   rebuilt faithfully as a vector from its measured layout.
 * - Ryder Picture Company: existing repo asset (public/media/trust/logo-05.png,
 *   removed). The RYDER wordmark and the pickup truck rebuilt as a clean
 *   vector from the original's measured geometry, without the filled box.
 * - Paramount: existing repo asset (public/media/trust/logo-06.svg, removed),
 *   the script wordmark recolored to one color.
 */
export const trustedByLogos: TrustLogo[] = [
  {
    id: "trust-logo-01",
    label: "Filmmaker Tools",
    src: "/media/trust/filmmaker-tools.svg",
    aspectRatio: 3.108,
    scale: 0.96,
  },
  {
    id: "trust-logo-02",
    label: "End Cue",
    src: "/media/trust/end-cue.svg",
    aspectRatio: 1.967,
    scale: 1.08,
  },
  {
    id: "trust-logo-03",
    label: "Greencard",
    src: "/media/trust/greencard.svg",
    aspectRatio: 5.533,
    scale: 0.74,
  },
  {
    id: "trust-logo-04",
    label: "Arte Facto",
    src: "/media/trust/arte-facto.svg",
    aspectRatio: 3.368,
    scale: 0.92,
  },
  {
    id: "trust-logo-05",
    label: "Ryder Picture Company",
    src: "/media/trust/ryder-picture-company.svg",
    aspectRatio: 1.653,
    scale: 1.2,
  },
  {
    id: "trust-logo-06",
    label: "Paramount",
    src: "/media/trust/paramount.svg",
    aspectRatio: 3.698,
    scale: 0.98,
  },
];

export const trustHubChips = [
  { id: "mfa", label: "MFA" },
  { id: "rbac", label: "RBAC" },
  { id: "share-links", label: "Share links" },
  { id: "audit", label: "Audit log" },
] as const;

export type SocialLinkId = "x" | "linkedin" | "youtube" | "tiktok";

export type SocialLink = {
  id: SocialLinkId;
  label: string;
  href: string;
};

export const socialLinks: SocialLink[] = [
  {
    id: "x",
    label: "X",
    href: "https://x.com/RivetAIHQ",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/rivetai",
  },
  {
    id: "youtube",
    label: "YouTube",
    href: "https://www.youtube.com/@rivetai",
  },
  {
    id: "tiktok",
    label: "TikTok",
    href: "https://www.tiktok.com/@rivetai",
  },
];
